import { isCancelled, isExternal } from '@reditelji/shared';
import type { TimelinePayload } from '../api/types';
import { fillTargets, shiftConfirmation } from '../timeline/derive';

export interface HeaderStats {
  coveragePct: number;
  confirmedShifts: number;
  confirmedPeople: number;
  waitingShifts: number;
  waitingPeople: number;
  rejectedShifts: number;
  rejectedPeople: number;
  /** Shifts an external team covers — outside the confirmation split. */
  externalShifts: number;
  externalPeople: number;
  uncovered: number;
}

/**
 * Header numbers: shifts (big) / the distinct people behind them (small). A
 * shift is counted by what was said about it (SPEC §1.1F), so somebody who
 * agreed to one shift today and has yet to answer another stands behind both
 * "potrjeno" and "čaka".
 */
export function deriveHeaderStats(data: TimelinePayload): HeaderStats {
  const volunteersById = new Map(data.volunteers.map((v) => [v.id, v]));
  const buckets = {
    potrjen: new Set<string>(),
    caka: new Set<string>(),
    zavrnjen: new Set<string>(),
    zunanja: new Set<string>(),
  };
  let confirmedShifts = 0;
  let externalShifts = 0;
  /** Kept by somebody who is not coming: covered, but nobody's confirmation. */
  let keptShifts = 0;
  let waitingShifts = 0;
  let rejectedShifts = 0;
  let totalShifts = 0;

  for (const p of data.posts) {
    for (const s of p.shifts) {
      totalShifts += 1;
      const v = s.volunteerId ? volunteersById.get(s.volunteerId) : undefined;
      if (!v || s.state === 'open') continue;
      const standing = shiftConfirmation(s, v);
      if (isExternal(v)) {
        // Covered, and nothing to confirm: counted apart so POKRITOST reads
        // true while the waiting figure stays a list of people to chase.
        externalShifts += 1;
        buckets.zunanja.add(v.id);
      } else if (isCancelled(v)) {
        // A shift somebody who is not coming kept — past, or under way with a
        // check-in (SPEC §1.1K). It covers the post; there is nobody to chase.
        keptShifts += 1;
      } else if (s.state === 'done' || standing === 'potrjen') {
        confirmedShifts += 1;
        buckets.potrjen.add(v.id);
      } else if (standing === 'zavrnjen') {
        rejectedShifts += 1;
        buckets.zavrnjen.add(v.id);
      } else {
        waitingShifts += 1;
        buckets.caka.add(v.id);
      }
    }
  }

  return {
    coveragePct:
      totalShifts === 0
        ? 100
        : Math.round(((confirmedShifts + externalShifts + keptShifts) / totalShifts) * 100),
    confirmedShifts,
    confirmedPeople: buckets.potrjen.size,
    externalShifts,
    externalPeople: buckets.zunanja.size,
    waitingShifts,
    waitingPeople: buckets.caka.size,
    rejectedShifts,
    rejectedPeople: buckets.zavrnjen.size,
    // Each uncovered stretch once: a PROSTO row, or a gap with the rows cut out.
    uncovered: fillTargets(data).length,
  };
}
